'use client';

import { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface DateRange {
  startDate: Date;
  endDate: Date;
  preset: string;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

const DateRangePicker = ({ value, onChange }: DateRangePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(value.preset);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const presets = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 },
    { label: 'This Quarter', days: 90 },
    { label: 'This Year', days: 365 }
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handlePresetSelect = (preset: { label: string; days: number }) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - preset.days);

    setSelectedPreset(preset.label);
    onChange({
      startDate,
      endDate,
      preset: preset.label
    });
    setIsOpen(false);
  };

  const formatDateRange = () => {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    const start = value.startDate.toLocaleDateString('en-US', options);
    const end = value.endDate.toLocaleDateString('en-US', options);
    return `${start} - ${end}`;
  };

  return (
    <div data-component-id="src/components/common/DateRangePicker.tsx:76:4" data-component-path="src/components/common/DateRangePicker.tsx" data-component-line="76" data-component-end-line="116" data-component-file="DateRangePicker.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative" ref={dropdownRef}>
      <button data-component-id="src/components/common/DateRangePicker.tsx:77:6" data-component-path="src/components/common/DateRangePicker.tsx" data-component-line="77" data-component-end-line="93" data-component-file="DateRangePicker.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20px-4%20py-2%20bg-secondary%20text-foreground%20rounded-lg%20border%20border-border%20transition-smooth%20hover%3Abg-muted%20hover%3Aborder-accent-cyan%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-accent-cyan%20focus%3Aring-offset-2%20focus%3Aring-offset-surface%22%7D"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 bg-secondary text-foreground rounded-lg border border-border transition-smooth hover:bg-muted hover:border-accent-cyan focus:outline-none focus:ring-2 focus:ring-accent-cyan focus:ring-offset-2 focus:ring-offset-surface"
        aria-label="Select date range"
        aria-expanded={isOpen}
      >
        <Icon data-component-id="src/components/common/DateRangePicker.tsx:83:8" data-component-path="src/components/common/DateRangePicker.tsx" data-component-line="83" data-component-end-line="83" data-component-file="DateRangePicker.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CalendarIcon%22%7D" name="CalendarIcon" size={20} />
        <div data-component-id="src/components/common/DateRangePicker.tsx:84:8" data-component-path="src/components/common/DateRangePicker.tsx" data-component-line="84" data-component-end-line="87" data-component-file="DateRangePicker.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-start%22%7D" className="flex flex-col items-start">
          <span data-component-id="src/components/common/DateRangePicker.tsx:85:10" data-component-path="src/components/common/DateRangePicker.tsx" data-component-line="85" data-component-end-line="85" data-component-file="DateRangePicker.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22caption%20text-xs%20text-text-secondary%22%7D" className="caption text-xs text-text-secondary">{selectedPreset}</span>
          <span data-component-id="src/components/common/DateRangePicker.tsx:86:10" data-component-path="src/components/common/DateRangePicker.tsx" data-component-line="86" data-component-end-line="86" data-component-file="DateRangePicker.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-body%20text-sm%20font-medium%20hidden%20sm%3Ablock%22%7D" className="font-body text-sm font-medium hidden sm:block">{formatDateRange()}</span>
        </div>
        <Icon data-component-id="src/components/common/DateRangePicker.tsx:88:8" data-component-path="src/components/common/DateRangePicker.tsx" data-component-line="88" data-component-end-line="92" data-component-file="DateRangePicker.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronDownIcon%22%7D" 
          name="ChevronDownIcon" 
          size={16} 
          className={`transition-smooth ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div data-component-id="src/components/common/DateRangePicker.tsx:96:8" data-component-path="src/components/common/DateRangePicker.tsx" data-component-line="96" data-component-end-line="114" data-component-file="DateRangePicker.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-full%20right-0%20mt-2%20w-64%20bg-popover%20rounded-lg%20border%20border-border%20shadow-navy-lg%20z-1010%20overflow-hidden%22%7D" className="absolute top-full right-0 mt-2 w-64 bg-popover rounded-lg border border-border shadow-navy-lg z-1010 overflow-hidden">
          <div data-component-id="src/components/common/DateRangePicker.tsx:97:10" data-component-path="src/components/common/DateRangePicker.tsx" data-component-line="97" data-component-end-line="113" data-component-file="DateRangePicker.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-2%22%7D" className="p-2">
            <div data-component-id="src/components/common/DateRangePicker.tsx:98:12" data-component-path="src/components/common/DateRangePicker.tsx" data-component-line="98" data-component-end-line="100" data-component-file="DateRangePicker.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22caption%20text-xs%20text-text-secondary%20px-3%20py-2%20font-medium%22%2C%22textContent%22%3A%22Quick%20Select%22%7D" className="caption text-xs text-text-secondary px-3 py-2 font-medium">
              Quick Select
            </div>
            {presets.map((preset) => (
              <button data-component-id="src/components/common/DateRangePicker.tsx:102:14" data-component-path="src/components/common/DateRangePicker.tsx" data-component-line="102" data-component-end-line="111" data-component-file="DateRangePicker.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                key={preset.label}
                onClick={() => handlePresetSelect(preset)}
                className={`w-full text-left px-3 py-2 rounded-md transition-smooth ${
                  selectedPreset === preset.label
                    ? 'bg-secondary text-accent-cyan' :'text-popover-foreground hover:bg-muted'
                }`}
              >
                <span data-component-id="src/components/common/DateRangePicker.tsx:110:16" data-component-path="src/components/common/DateRangePicker.tsx" data-component-line="110" data-component-end-line="110" data-component-file="DateRangePicker.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-body%20text-sm%22%7D" className="font-body text-sm">{preset.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;